import { z } from 'zod';
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import i18next from 'i18next';

/**
 * Register form validation schema
 * Phone number is now OPTIONAL - can be added later in profile settings
 * Uses Zod for runtime type validation with i18n support
 */

export const registerSchema = z
  .object({
    firstName: z
      .string()
      .min(1, { message: () => i18next.t('validation.firstNameRequired', { ns: 'auth' }) })
      .min(2, { message: () => i18next.t('validation.firstNameMinLength', { ns: 'auth' }) })
      .max(50, { message: () => i18next.t('validation.firstNameMaxLength', { ns: 'auth' }) })
      .regex(/^[a-zA-Z\u0600-\u06FF\s'-]+$/, { message: () => i18next.t('validation.firstNameInvalid', { ns: 'auth' }) }),

    lastName: z
      .string()
      .min(1, { message: () => i18next.t('validation.lastNameRequired', { ns: 'auth' }) })
      .min(2, { message: () => i18next.t('validation.lastNameMinLength', { ns: 'auth' }) })
      .max(50, { message: () => i18next.t('validation.lastNameMaxLength', { ns: 'auth' }) })
      .regex(/^[a-zA-Z\u0600-\u06FF\s'-]+$/, { message: () => i18next.t('validation.lastNameInvalid', { ns: 'auth' }) }),

    email: z
      .string()
      .min(1, { message: () => i18next.t('validation.emailRequired', { ns: 'auth' }) })
      .email({ message: () => i18next.t('validation.emailInvalid', { ns: 'auth' }) })
      .max(100, { message: () => i18next.t('validation.emailMaxLength', { ns: 'auth' }) })
      .toLowerCase(),

    // OPTIONAL: Phone number - kept for future use in profile settings
    // phoneNumber: z
    //   .string()
    //   .min(1, { message: () => i18next.t('validation.phoneRequired', { ns: 'auth' }) })
    //   .refine((value) => {
    //     // Validate phone number format based on country
    //     try {
    //       if (!value.startsWith('+')) {
    //         return false;
    //       }
    //       return isValidPhoneNumber(value);
    //     } catch {
    //       return false;
    //     }
    //   }, { message: () => i18next.t('validation.phoneInvalid', { ns: 'auth' }) })
    //   .refine((value) => {
    //     // Additional validation: parse and check format
    //     try {
    //       const phoneNumber = parsePhoneNumber(value);
    //       return phoneNumber && phoneNumber.isValid();
    //     } catch {
    //       return false;
    //     }
    //   }, { message: () => i18next.t('validation.phoneFormat', { ns: 'auth' }) }),

    gender: z
      .string()
      .min(1, { message: () => i18next.t('validation.genderRequired', { ns: 'auth' }) })
      .refine((val) => ['male', 'female', 'other'].includes(val), {
        message: () => i18next.t('validation.genderInvalid', { ns: 'auth' }),
      })
      .transform((val) => val as 'male' | 'female' | 'other'),

    dateOfBirth: z
      .string()
      .min(1, { message: () => i18next.t('validation.dobRequired', { ns: 'auth' }) })
      .refine((date) => {
        const birthDate = new Date(date);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        const dayDiff = today.getDate() - birthDate.getDate();

        // Check if user is at least 13 years old
        if (age > 13) return true;
        if (age === 13 && monthDiff > 0) return true;
        if (age === 13 && monthDiff === 0 && dayDiff >= 0) return true;

        return false;
      }, { message: () => i18next.t('validation.dobMinAge', { ns: 'auth' }) })
      .refine((date) => {
        const birthDate = new Date(date);
        const today = new Date();

        // Check if date is not in the future
        return birthDate <= today;
      }, { message: () => i18next.t('validation.dobFuture', { ns: 'auth' }) }),

    password: z
      .string()
      .min(1, { message: () => i18next.t('validation.passwordRequired', { ns: 'auth' }) })
      .min(8, { message: () => i18next.t('validation.passwordMinLength', { ns: 'auth', min: 8 }) })
      .max(128, { message: () => i18next.t('validation.passwordMaxLength', { ns: 'auth', max: 128 }) })
      .regex(/[a-z]/, { message: () => i18next.t('validation.passwordLowercase', { ns: 'auth' }) })
      .regex(/[A-Z]/, { message: () => i18next.t('validation.passwordUppercase', { ns: 'auth' }) })
      .regex(/[0-9]/, { message: () => i18next.t('validation.passwordNumber', { ns: 'auth' }) })
      .regex(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, { message: () => i18next.t('validation.passwordSpecial', { ns: 'auth' }) }),

    confirmPassword: z
      .string()
      .min(1, { message: () => i18next.t('validation.confirmPasswordRequired', { ns: 'auth' }) }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: () => i18next.t('validation.passwordMismatch', { ns: 'auth' }),
    path: ['confirmPassword'], // Set error on confirmPassword field
  });

export type RegisterFormData = z.infer<typeof registerSchema>;
